import { Component, DOCUMENT, EventEmitter, inject, Input, Output, Renderer2 } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PermissionService } from '@/app/core/services/permission/permission.service';
import { PERMISSION_LIST } from '@/app/core/constants/constants';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  readonly PERMISSIONS = PERMISSION_LIST;
  get homeRoute(): string {
    return this.permissionService.getFirstAccessibleRoute();
  }

  @Input() isCollapsed = false;
  private readonly mastersChildRoutes = [
    '/countries',
    '/states',
    '/cities',
    '/roles',
    '/lead-sources',
    '/project-master',
    '/property-configurations',
    '/disposition-categories',
    '/area-master',
    '/floor-details',
    '/furnishing-status',
    '/property-purpose',
    '/possession',
  ];

  readonly reportRoute = '/reports/follow-ups-pending';

  constructor() {
    this.syncMastersOpenState(this.router.url);
    // Auto-close Master Data on any navigation
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      // this.isMasterDataOpen = false;
      this.syncMastersOpenState(this.router.url);
      if (this.isMobileOpen) {
        this.closeMobile.emit();
      }
    });
  }
  isMasterDataOpen = false;
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.renderer.addClass(this.document.body, 'sidebar-collapsed');
    } else {
      this.renderer.removeClass(this.document.body, 'sidebar-collapsed');
    }
  }
  @Input() isMobileOpen = false;
  @Output() closeMobile = new EventEmitter<void>();

  toggleMasterData(): void {
    // if (this.isCollapsed) return; // prevent dropdown in collapsed mode
    this.isMasterDataOpen = !this.isMasterDataOpen;
  }

  get isMastersActive(): boolean {
    return this.isMastersChildUrl(this.router.url);
  }

  private isMastersChildUrl(url: string): boolean {
    const cleanUrl = (url || '').split('?')[0].split('#')[0];
    return this.mastersChildRoutes.some(
      (route) => cleanUrl === route || cleanUrl.startsWith(`${route}/`),
    );
  }

  private syncMastersOpenState(url: string): void {
    if (this.isMastersChildUrl(url)) this.isMasterDataOpen = true;
  }

  has(permission: string): boolean {
    return this.permissionService.has(permission);
  }

  hasAny(permissions: string[]): boolean {
    return this.permissionService.hasAny(permissions);
  }

  canAccessCallbackQueue(): boolean {
    return (
      this.permissionService.has(this.PERMISSIONS.AGENT_LEAD.VIEW) &&
      this.permissionService.isAgentLogin()
    );
  }

  canAccessRecordings(): boolean {
    return this.hasAny([
      this.PERMISSIONS.RECORDING.PLAYBACK_ALL,
      this.PERMISSIONS.RECORDING.PLAYBACK_OWN,
    ]);
  }

  canAccessMasters(): boolean {
    return this.hasAny([
      this.PERMISSIONS.LOCATION.COUNTRY_VIEW,
      this.PERMISSIONS.LOCATION.STATE_VIEW,
      this.PERMISSIONS.LOCATION.CITY_VIEW,
      this.PERMISSIONS.ROLE.VIEW,
      this.PERMISSIONS.LEAD_SOURCE.VIEW,
      this.PERMISSIONS.PROJECT.VIEW,
      this.PERMISSIONS.PROPERTY_CONFIGURATION.VIEW,
      this.PERMISSIONS.DISPOSITION_CATEGORY.VIEW,
      this.PERMISSIONS.AREA_MASTER.VIEW,
      this.PERMISSIONS.FLOOR_DETAIL.VIEW,
      this.PERMISSIONS.FURNISHING_STATUS.VIEW,
      this.PERMISSIONS.PROPERTY_PURPOSE.VIEW,
      this.PERMISSIONS.POSSESSION.VIEW,
    ]);
  }
}
